<template>
    <div>
       <div class="sign-up-container"  v-if="!showActivationForm">
        <h1>{{ $t("signInUp.createAccount") }}</h1>
        <div
            class="intro-signUp"
            v-if="$t('signInUp.introSignUp').length"
            v-html="$t('signInUp.introSignUp')"
        ></div>
        <form @submit.prevent="submitForm">
            <input type="text" v-model="firstName" :placeholder="$t('signInUp.firstName')" />
            <input type="text" v-model="lastName" :placeholder="$t('signInUp.lastName')" />
            <input
                type="email"
                v-model="email"
                @input="validateEmail"
                :placeholder="$t('signInUp.emailAddress')"
            />
            <div>
                <label class="password-area" for="password_reg">
                    <input
                        id="password_reg"
                        :type="passType"
                        v-model="password1"
                        :placeholder="$t('signInUp.password')"
                    />
                    <i
                        :class="isPassHidden ? 'icon-eye-blocked' : 'icon-eye'"
                        @click="showPassword"
                    ></i>
                </label>

                <ul v-if="passwordInvalid" class="pwdValidationList">
                    <li
                        v-for="key in Object.keys(passwordValidations)"
                        :key="key"
                        class="validationMessage"
                    >
                        <i
                            :class="{
                                'icon-close': !passwordValidations[key],
                                'icon-checkmark': passwordValidations[key]
                            }"
                        ></i>
                        <span>
                            {{ $t("signInUp." + key) }}
                        </span>
                    </li>
                </ul>
            </div>
            <input
                :type="passType"
                v-model="password2"
                :placeholder="$t('signInUp.passwordConfirm')"
            />

            <div class="validationMessage" v-if="password2.length > 0 && pwdNoMatch">
                {{ $t("signInUp.pwdNoMatch") }}
            </div>

            <transition name="fade" mode="out-in">
                <div class="submit-container" v-if="!isPending">
                    <input
                        class="submit-button"
                        type="submit"
                        :disabled="formInvalid"
                        :value="$t('general.validate')"
                    />

                    <div class="delimiter">
                        <hr />
                        <span>
                            {{ $t("general.or").toUpperCase() }}
                        </span>
                        <hr />
                    </div>

                    <transition name="fade">
                        <div v-if="error.length > 0" class="error-message">
                            {{ error }}
                        </div>
                    </transition>

                    <router-link
                        class="signin-link"
                        to="/SignIn"
                        replace
                        @click.prevent="$router.replace('/SignIn')"
                        >{{ $t("signInUp.alreadyHaveAccount") }}</router-link
                    >

                    <RemindsMeCheckbox :value="remindsMe" @input="updateRemindsMe" />
                </div>
                <Loader class="loader" v-else />
            </transition>
        </form>
        <transition name="fade" appear>
            <div v-if="messageText.length > 0" class="info-message-container">
                <div class="info-message-content">
                    <h3 v-if="messageTitle.length > 0">
                        {{ messageTitle }}
                    </h3>
                    <p>
                        {{ messageText }}
                    </p>

                    <button @click.prevent="closeMessagePopin" class="close-message-button">
                        OK
                    </button>
                </div>
            </div>
        </transition>
        <div class="view-footer">
            <RGPDLink />
            <GcuLink class="gcu-link" v-if="$t('general.GCU').length" />
        </div>
        <transition name="fade" v-if="rgpdPopinDisplayed">
            <RGPDPopin @close="onRGPDClosed"></RGPDPopin>
        </transition>
       </div>
        <div v-if="showActivationForm" class="sign-up-container">
            <h1>{{ $t("signInUp.activateAccount") }}</h1>
            <div
                class="intro-signUp"
            >{{$t('signInUp.introActivate')}}</div>
            <form>
                <input type="text" v-model="code" :placeholder="$t('signInUp.activationCode')" />
                <transition name="fade" mode="out-in">
                    <div class="submit-container" v-if="!isPending">
                        <button
                            class="submit-button"
                            :disabled="activateformInvalid"
                            @click.prevent="sendCode"
                        >{{$t('general.send')}}</button>
    
                        <div>
                            <transition name="fade" >
                                <div v-if="error.length > 0" class="error-message">
                                    {{ error }}
                                </div>
                            </transition>
                        </div>
                    </div>
                </transition>
            </form>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapState } from "vuex";

import Loader from "@/components/Loader.vue";
import RGPDLink from "@/components/RGPDLink.vue";
import GcuLink from "@/components/gcuLink.vue";
import RGPDPopin from "@/components/RGPDPopin.vue";
import { State } from "@/store";
import Consents from "@/models/Consents";
import RemindsMeCheckbox from "@/components/RemindsMeCheckbox.vue";

export default defineComponent({
    name: "Signup",
    components: {
        Loader,
        GcuLink,
        RGPDLink,
        RGPDPopin,
        RemindsMeCheckbox
    },
    data() {
        return {
            firstName: "",
            lastName: "",
            email: "",
            password1: "",
            password2: "",
            passType: `password`,
            isPassHidden: true,
            isPending: false,
            error: "",
            messageTitle: "",
            messageText: "",
            messagePromiseResolve: null as Function | null,
            rgpdPopinDisplayed: false,
            termsAndConditionsConsent: null,
            shareEmailConsent: null,
            remindsMe: false,
            passwordInvalid: false,
            passwordValidations: {
                pwdLength: false,
                pwdNumberRequired: false,
                pwdUpperRequired: false,
                pwdNoSpace: false
            },
            pwdNoMatch: false,
            showActivationForm: false,
            code: ""
        };
    },
    computed: {
        formInvalid() {
            return (
                !this.firstName.length ||
                !this.lastName.length ||
                !this.email.length ||
                !this.password1.length ||
                this.passwordInvalid ||
                this.pwdNoMatch
            );
        },
        activateformInvalid() {
            return (
                !this.code.length
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            authenticated: (state: State) => state.auth.authenticated,
            guest: (state: State) => state.auth.guest,
            didSignUp: (state: State) => state.auth.didSignUp
        })
    },
    watch: {
        async authenticated() {
            if (this.authenticated) {
                this.isPending = false;

                if (this.didSignUp) {
                    await this.$store.dispatch("rgpd/updateConsents", {
                        qrcodeId: this.qrcodeObject.qrcodeId,
                        shareEmailConsent: this.shareEmailConsent,
                        termsAndConditionsConsent: this.termsAndConditionsConsent
                    });

                    if (
                        this.$t("signInUp.onSignUpPopinText").length ||
                        this.$t("signInUp.onSignUpPopinTitle")
                    ) {
                        this.displayMessage(
                            this.$t("signInUp.onSignUpPopinText"),
                            this.$t("signInUp.onSignUpPopinTitle")
                        ).then(() => {
                            this.$router.replace("Upload");
                        });
                    } else {
                        this.$router.replace("Upload");
                    }
                } else {
                    this.$router.replace("Upload");
                }
            }
        },

        password1(value) {
            this.validatePwd(value);
        },

        password2() {
            this.pwdNoMatch = this.password1 !== this.password2;
        }
    },
    mounted() {
        if (!this.qrcodeObject.project.isActive) {
            this.$router.replace("/signin");
        }
    },
    methods: {
        async submitForm() {
            this.displayRGPDPopin();
        },

        showPassword() {
            if (this.isPassHidden) {
                this.passType = `text`;
                this.isPassHidden = false;
            } else {
                this.passType = `password`;
                this.isPassHidden = true;
            }
        },

        displayMessage(text: string, title = ""): Promise<void> {
            return new Promise(resolve => {
                this.messagePromiseResolve = resolve;
                this.messageText = text;
                this.messageTitle = title;
            });
        },

        closeMessagePopin() {
            this.messageText = "";
            this.messageTitle = "";

            if (this.messagePromiseResolve) {
                this.messagePromiseResolve();
                this.messagePromiseResolve = null;
            }
        },

        displayRGPDPopin() {
            this.rgpdPopinDisplayed = true;
        },

        async onRGPDClosed(consents: Consents) {
            this.rgpdPopinDisplayed = false;

            if (!consents.termsAndConditionsConsent) {
                this.displayRGPDPopin();
                return;
            }

            this.error = "";
            this.isPending = true;
            try {
                this.termsAndConditionsConsent = consents.termsAndConditionsConsent;
                this.shareEmailConsent = consents.shareEmailConsent;

                await this.$store.dispatch("auth/register", {
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    password: this.password1,
                    remindsMe: this.remindsMe
                });
                this.showActivationForm = true;
                this.isPending = false;
            } catch (e) {
                this.isPending = false;
                this.error = this.$t("signInUp." + e.message);
            }
        },

        async sendCode(){
            this.$store.dispatch("auth/check");
            try{
                if(this.guest){
                    await this.$store.dispatch("auth/activate", {
                        id: this.guest.id,
                        code: this.code,
                    });
                    this.$router.replace("/signin");
                }
            } catch(e){
                this.error = this.$t("signInUp.activationError") + e;
            }
        },

        updateRemindsMe(value) {
            this.remindsMe = value;
        },

        validatePwd(value) {
            const numberRegex = /\d/;
            const caseRegex = /(?=.*[a-z])(?=.*[A-Z])/;
            const lengthRegex = /.{8,12}/;
            const noSpaceRegex = /^[^\s]*$/;

            this.passwordValidations.pwdLength = lengthRegex.test(value);
            this.passwordValidations.pwdUpperRequired = caseRegex.test(value);
            this.passwordValidations.pwdNumberRequired = numberRegex.test(value);
            this.passwordValidations.pwdNoSpace = noSpaceRegex.test(value);

            this.passwordInvalid =
                Object.values(this.passwordValidations).find(value => value === false) !==
                undefined;

            this.pwdNoMatch = this.password1 !== this.password2;
        },

        validateEmail(ev: Event) {
            const input = ev.target as HTMLInputElement;
            input.value = input.value.toLowerCase();
        }
    }
});
</script>

<style scoped>
h1 {
    margin: 0;
    color: var(--screenTextColor);
}

.sign-up-container,
.sign-up-container form {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 15px;
    align-items: center;
}

.submit-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.intro-signUp {
    color: var(--screenTextColor);
    margin: 5px 15px;
}

.signin-link {
    color: var(--screenTextColor);
    grid-column: 1 / 3;
}

input[type="text"],
input[type="email"],
input[type="password"] {
    box-sizing: border-box;
    max-width: 185px;
    padding: 10px 20px;
    border-radius: calc(1em + 10px);
    line-height: 1em;
    background: var(--white);
    color: var(--black);
    border: 0;
    margin-left: auto;
    margin-right: auto;
    box-shadow: 2px 3px 5px 0px rgb(150 150 150 / 50%);
}
input[type="password"],
input[type="text"] {
    padding-right: 40px;
}

.password-area {
    position: relative;
}

.icon-eye-blocked,
.icon-eye {
    content: "";
    position: absolute;
    top: 4px;
    right: 14px;
    border-radius: 50%;
    transition: background-color 0.3s;
}

.icon-eye:hover,
.icon-eye-blocked:hover {
    background-color: rgba(0, 0, 0, 0.1);
}

input[type="submit"], .submit-button {
    margin-bottom: 5px;
    padding: 10px 20px;
    border-radius: calc(1em + 10px);
    line-height: 1em;
    border: 0;
    background: var(--secondary);
    color: var(--buttonTextColor);
    transition: filter 0.2s ease-in;
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
}

input[type="submit"]:disabled, .submit-button:disabled {
    opacity: 0.5;
}

.loader {
    margin: auto;
}

.error-message {
    color: var(--red);
    margin-bottom: 10px;
}

.info-message-container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.info-message-content {
    width: 80%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: var(--white);
    border-radius: 10px;
    color: var(--black);
    padding: 20px;
}

.info-message-content h3 {
    width: 70%;
    margin-top: 0;
}

.info-message-content p {
    font-weight: 600;
    line-height: 1.5em;
}

.close-message-button {
    width: 90%;
    max-width: 400px;
    background: var(--white);
    padding: 7px 0;

    color: var(--black);
    font-weight: bold;
    font-size: 1.2em;

    border: 1px solid black;
    border-radius: 6px;
}

.view-footer {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
}

ul.pwdValidationList {
    list-style: none;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

ul.pwdValidationList li {
    margin-top: 8px;
    text-align: left;
    display: grid;
    grid-template-columns: auto auto;
    gap: 8px;
}

.validationMessage {
    font-size: 0.8em;
    color: var(--screenTextColor);
}

.google-btn {
    display: flex;
    flex-direction: row;
    height: 42px;
    background-color: #4285f4;
    border-radius: 2px;
    margin-bottom: 30px;
}

.google-btn:active {
    background: #1669f2;
}

.google-btn .google-icon-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    /* position: absolute; */
    margin-top: 1px;
    margin-left: 1px;
    width: 40px;
    height: 40px;
    border-radius: 2px;
    background-color: var(--white);
}

.google-btn .google-icon {
    width: 18px;
    height: 18px;
}

.google-btn .btn-text {
    padding: 0 10px;
    color: var(--white);
    font-size: 14px;
    letter-spacing: 0.2px;
    font-family: "Roboto";
}

.delimiter {
    display: flex;
    flex-direction: row;
    padding-left: 15px;
    padding-right: 15px;
    width: calc(100% - 30px);
    margin-top: 10px;
    margin-bottom: 10px;
}

.delimiter hr {
    flex-grow: 1;
    height: 1px;
    border: 0;
    border-top: 1px solid var(--screenTextColor);
    opacity: 0.6;
}

.delimiter span {
    flex-grow: 0;
    margin-left: 5px;
    margin-right: 5px;
    font-weight: bold;
    color: var(--screenTextColor);
}

@import url(https://fonts.googleapis.com/css?family=Roboto:500);
</style>
<style>
/* because link added with v-html, not concerned by scoped style */
.intro-signUp a {
    color: var(--screenTextColor) !important;
}
</style>
